import { CheckCircle2, Circle, CircleDot, XCircle } from "lucide-react"

import { cn } from "@/lib/utils"

export interface OrderPipelineStep {
  index: number
  label: string
}

interface OrderPipelineProps {
  steps: OrderPipelineStep[]
  currentStepIndex: number
  title?: string
  className?: string
}

export function OrderPipeline({ steps, currentStepIndex, title, className }: OrderPipelineProps) {
  return (
    <div className={cn("space-y-6 p-6", className)}>
      {title && (
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          {title}
        </span>
      )}
      <div className="relative pl-2 pt-2">
        <div className="absolute bottom-6 left-6 top-4 z-0 w-px bg-border" />
        <div className="space-y-5">
          {steps.map((step, index) => {
            const isCompleted = currentStepIndex > step.index
            const isCurrent = currentStepIndex === step.index
            const isError = currentStepIndex === -1 && index === 0

            let Icon = Circle
            let iconClass = "bg-background text-muted-foreground"

            if (isCompleted) {
              Icon = CheckCircle2
              iconClass = "bg-background text-emerald-500"
            } else if (isCurrent) {
              Icon = isError ? XCircle : CircleDot
              iconClass = isError ? "bg-background text-destructive" : "bg-background text-primary"
            } else if (currentStepIndex === -1 && index > 0) {
              iconClass = "bg-background text-muted-foreground/30"
            }

            return (
              <div
                key={`${step.index}-${step.label}`}
                className="relative z-10 flex items-center gap-4"
              >
                <div className="relative shrink-0 rounded-full bg-background">
                  <Icon className={cn("h-8 w-8", iconClass)} />
                </div>
                <span
                  className={cn(
                    "text-sm font-medium tracking-wide transition-colors",
                    isCurrent
                      ? "font-bold text-foreground"
                      : isCompleted
                        ? "text-foreground"
                        : "text-muted-foreground",
                    currentStepIndex === -1 && index > 0 && "opacity-50",
                    isError && "text-destructive"
                  )}
                >
                  {step.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
